import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { CaseApiService } from '@api-new/caseservice';
import { ENUM_PropertyInvestmentType } from '@api-new/common';
import { HTTP_CP_SmartSearch, HTTP_X_CalculatePayMortgageFasterSavings_Response, MortgageApiService } from '@api-new/mortgageservice';
import { OnStoreInit } from '@ngrx/component-store';
import { tapResponse } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { paths } from '@platform/paths';
import { createSmartSearchFormToHTTPRequestAdapter } from '@platform/smart-search/adapters/createSmartSearchFormToHTTPRequest.adapter';
import { createSmartSearchHelpCaseFormFormToHTTPRequest } from '@platform/smart-search/adapters/createSmartSearchHelpCaseFormFormToHTTPRequest.adapter';
import { AddSmartSearchDialogStep } from '@platform/smart-search/enums/add-smart-search-dialog-step.enum';
import { SmartSearchType } from '@platform/smart-search/enums/smart-search-type.enum';
import { PredefinedAddSmartSearchForm } from '@platform/smart-search/models/predefined-add-smart-search-form.model';
import {
  selectAddSmartSearchDialogClientUserData,
  selectHasClientResidentialProperty,
} from '@platform/smart-search/pages/add-smart-search-dialog/add-smart-search-dialog.selectors';
import { AddSmartSearchDialogFormRawValues } from '@platform/smart-search/utils/formUtils';
import { ScreenSizeBreakpointObserverService } from '@shared-lib/services/screen-size-breakpoint-observer.service';
import { ToastService, ToastSeverity } from '@shared-lib/services/toast.service';
import { CommonDialogState, CommonDialogStore } from '@shared-lib/store/common-dialog.store';
import { ErrorModel } from '@shared/models/app-state.model';
import { selectIsLoadingPlatformItems, selectPropertyMortgageSelectItemsForAddSmartSearch } from '@shared/store/common.selectors';
import { getHelpCases } from '@shared/store/help-cases/help-cases.actions';
import { getSmartSearches } from '@shared/store/smart-searches/smart-searches.actions';
import { DynamicDialogConfig } from 'primeng/dynamicdialog';
import { Observable } from 'rxjs';
import { map, switchMap, tap, withLatestFrom } from 'rxjs/operators';

interface AddSmartSearchDialogState extends CommonDialogState {
  formValue: AddSmartSearchDialogFormRawValues;
  dialogStep: AddSmartSearchDialogStep;
  startingDialogStep: AddSmartSearchDialogStep;
  payMortgageFasterSaving: HTTP_X_CalculatePayMortgageFasterSavings_Response;
  loadingActions: {
    isSaveInProgress: boolean;
    isLoadingPayMortgageFasterSaving: boolean;
  };
}

const initialAddSmartSearchDialogState: AddSmartSearchDialogState = {
  formValue: null,
  dialogStep: AddSmartSearchDialogStep.INITIAL,
  startingDialogStep: AddSmartSearchDialogStep.INITIAL,
  payMortgageFasterSaving: null,
  isDialogPositionUp: false,
  loadingActions: {
    isSaveInProgress: false,
    isLoadingPayMortgageFasterSaving: false,
  },
};

@Injectable()
export class AddSmartSearchDialogStore extends CommonDialogStore<AddSmartSearchDialogState, HTTP_CP_SmartSearch> implements OnStoreInit {
  private readonly dialogConfig = inject(DynamicDialogConfig);
  private readonly screenSizeBreakpointObserverService = inject(ScreenSizeBreakpointObserverService);
  private readonly globalStore = inject(Store);
  private readonly mortgageApiService = inject(MortgageApiService);
  private readonly caseApiService = inject(CaseApiService);
  private readonly router = inject(Router);
  private readonly toastService = inject(ToastService);

  constructor() {
    super(initialAddSmartSearchDialogState);
  }

  ngrxOnStoreInit(): void {
    const predefinedSmartSearch: PredefinedAddSmartSearchForm = this.dialogConfig.data?.predefinedSmartSearch;
    if (predefinedSmartSearch) {
      this.setPredefinedSmartSearchForm(predefinedSmartSearch);
      setTimeout(() => this.setDialogStep(predefinedSmartSearch.dialogStep));
    } else {
      this.setDialogStep(AddSmartSearchDialogStep.INITIAL);
    }
  }

  // SELECTORS ---------------------------------------------------------------------------------------------------------
  readonly formValue$ = this.select((state) => state.formValue);
  readonly dialogStep$ = this.select((state) => state.dialogStep);
  readonly startingDialogStep$ = this.select((state) => state.startingDialogStep);
  readonly isSkipPropertySelectPossible$ = this.select(
    (state) => state.formValue.typeSelection.type === SmartSearchType.SPEAK_TO_AN_EXPERT,
  );

  readonly payMortgageFasterSaving$ = this.select((state) => state.payMortgageFasterSaving);
  readonly isLoadingPayMortgageFasterSaving$ = this.select((state) => state.loadingActions.isLoadingPayMortgageFasterSaving);
  readonly isSaveInProgress$ = this.select((state) => state.loadingActions.isSaveInProgress);

  readonly screenSizeBreakpointName$ = this.screenSizeBreakpointObserverService.screenSizeBreakpointName$;

  readonly propertyWithMortgageSelectItems$ = this.globalStore.select(selectPropertyMortgageSelectItemsForAddSmartSearch);
  readonly isLoadingPlatformItems$ = this.globalStore.select(selectIsLoadingPlatformItems);
  readonly clientUserData$ = this.globalStore.select(selectAddSmartSearchDialogClientUserData);
  readonly hasClientResidentialProperty$ = this.globalStore.select(selectHasClientResidentialProperty);

  // ACTIONS -----------------------------------------------------------------------------------------------------------
  readonly nextStep = this.effect((nextStep$: Observable<void>) =>
    nextStep$.pipe(
      withLatestFrom(this.dialogStep$, this.formValue$),
      map(([, dialogStep, formValues]) => this.getNextStep(dialogStep, formValues)),
      tap((dialogStep: AddSmartSearchDialogStep) => {
        this.setDialogStep(dialogStep);
      }),
    ),
  );

  readonly previousStep = this.effect((previousStep$: Observable<void>) =>
    previousStep$.pipe(
      withLatestFrom(this.dialogStep$, this.formValue$),
      map(([, dialogStep, formValues]) => this.getPreviousStep(dialogStep, formValues)),
      tap((dialogStep: AddSmartSearchDialogStep) => {
        this.setDialogStep(dialogStep);
      }),
    ),
  );

  readonly createSmartSearch = this.effect((createSmartSearch$: Observable<void>) =>
    createSmartSearch$.pipe(
      withLatestFrom(this.formValue$, this.dialogStep$),
      tap(() => this.setIsSaveInProgress(true)),
      switchMap(([, formValues]) =>
        this.mortgageApiService.HTTP_CP_CreateSmartSearch(createSmartSearchFormToHTTPRequestAdapter(formValues)).pipe(
          tapResponse(
            (response) => {
              this.setIsSaveInProgress(false);
              this.dialogRef.close();
              this.globalStore.dispatch(getSmartSearches());
              void this.router.navigate([paths.PLATFORM, paths.platform.SMART_SEARCH, response.id]);
              this.toastService.showToast(ToastSeverity.success, 'Your adviser will get in touch with you as soon as they can');
            },
            (error) => {
              console.error(error);
              this.toastService.showToast(ToastSeverity.error, "We're sorry, something went wrong. Please try again later.");
              this.setIsSaveInProgress(false);
            },
          ),
        ),
      ),
    ),
  );

  readonly calculatePayMortgageFasterSaving = this.effect(
    (calculatePayMortgageFasterSaving$: Observable<{ termInMonths?: number; monthlyPaymentIncrease?: number }>) =>
      calculatePayMortgageFasterSaving$.pipe(
        withLatestFrom(this.formValue$),
        tap(() => this.setIsLoadingPayMortgageFasterSaving(true)),
        switchMap(([calculateSavingData, formValues]) =>
          this.mortgageApiService
            .HTTP_X_CalculatePayMortgageFasterSavings({
              propertyId: formValues.propertySelection.propertyMortgageSelectItem.propertyId,
              monthlyPaymentIncrease: calculateSavingData.monthlyPaymentIncrease,
              newTermInMonths: calculateSavingData.termInMonths,
            })
            .pipe(
              tapResponse(
                (response) => {
                  this.setPayMortgageFasterSaving(response);
                  this.setIsLoadingPayMortgageFasterSaving(false);
                },
                (error: ErrorModel) => {
                  console.error(error);
                  this.toastService.showToast(
                    ToastSeverity.error,
                    (error.error && error.error.message) || 'There was an error during calculation of insights',
                  );
                  this.setIsLoadingPayMortgageFasterSaving(false);
                },
              ),
            ),
        ),
      ),
  );

  readonly createHelpCase = this.effect((createSmartSearch$: Observable<void>) =>
    createSmartSearch$.pipe(
      withLatestFrom(this.formValue$, this.clientUserData$),
      tap(() => this.setIsSaveInProgress(true)),
      switchMap(([, formValues, { firstName, lastName }]) =>
        this.caseApiService
          .HTTP_CP_CreateHelpCase(createSmartSearchHelpCaseFormFormToHTTPRequest(formValues, { firstName, lastName }))
          .pipe(
            tapResponse(
              () => {
                this.setIsSaveInProgress(false);
                this.dialogRef.close(null);
                void this.router.navigate([paths.PLATFORM, paths.platform.PROFILE, paths.platform.profile.ADVISOR]);
                this.globalStore.dispatch(getHelpCases());
                this.toastService.showToast(ToastSeverity.success, 'Your adviser will get in touch with you as soon as they can');
              },
              (error) => console.error(error),
            ),
          ),
      ),
    ),
  );

  // REDUCER -----------------------------------------------------------------------------------------------------------
  readonly setDialogStep = this.updater<AddSmartSearchDialogStep>((state, dialogStep) => ({
    ...state,
    dialogStep,
  }));

  readonly setFormValue = this.updater<AddSmartSearchDialogFormRawValues>((state, formValue) => ({
    ...state,
    formValue,
  }));

  readonly setPredefinedSmartSearchForm = this.updater<PredefinedAddSmartSearchForm>(
    (state, predefinedSmartSearch): AddSmartSearchDialogState => ({
      ...state,
      formValue: { ...state.formValue, ...predefinedSmartSearch.addSmartSearchDialogFormRawValues },
      startingDialogStep: predefinedSmartSearch.dialogStep,
    }),
  );

  readonly setPayMortgageFasterSaving = this.updater<HTTP_X_CalculatePayMortgageFasterSavings_Response>(
    (state, payMortgageFasterSaving) => ({
      ...state,
      payMortgageFasterSaving,
      formValue: {
        ...state.formValue,
        remortgagePayMortgageFaster: {
          ...state.formValue.remortgagePayMortgageFaster,
          termInMonths: payMortgageFasterSaving.newTermInMonths,
          monthlyPaymentIncrease: payMortgageFasterSaving.monthlyPaymentIncrease,
        },
      },
    }),
  );

  readonly setIsLoadingPayMortgageFasterSaving = this.updater((state, isLoadingPayMortgageFasterSaving: boolean) => ({
    ...state,
    loadingActions: {
      ...state.loadingActions,
      isLoadingPayMortgageFasterSaving,
    },
  }));

  readonly setIsSaveInProgress = this.updater((state, isSaveInProgress: boolean) => ({
    ...state,
    loadingActions: {
      ...state.loadingActions,
      isSaveInProgress,
    },
  }));

  // HELPER ------------------------------------------------------------------------------------------------------------

  private getNextStep = (currentStep: AddSmartSearchDialogStep, formValue: AddSmartSearchDialogFormRawValues): AddSmartSearchDialogStep => {
    switch (currentStep) {
      case AddSmartSearchDialogStep.INITIAL: {
        if (formValue.typeSelection.type === SmartSearchType.NEW_PROPERTY_MAXIMUM_PRICE) {
          return AddSmartSearchDialogStep.NEW_PROPERTY_INVESTMENT_TYPE_SELECTION;
        }
        return AddSmartSearchDialogStep.PROPERTY_SELECTION;
      }
      case AddSmartSearchDialogStep.NEW_PROPERTY_INVESTMENT_TYPE_SELECTION: {
        if (formValue.newPropertyInvestmentTypeSelection.type === ENUM_PropertyInvestmentType.PROPERTY_INVESTMENT_TYPE_RESIDENTIAL) {
          return AddSmartSearchDialogStep.NEW_PROPERTY_MORTGAGE_PURPOSE_SELECTION;
        }
        return AddSmartSearchDialogStep.NEW_PROPERTY_MAXIMUM_PRICE;
      }
      case AddSmartSearchDialogStep.NEW_PROPERTY_MORTGAGE_PURPOSE_SELECTION: {
        return AddSmartSearchDialogStep.NEW_PROPERTY_MAXIMUM_PRICE;
      }
      case AddSmartSearchDialogStep.NEW_PROPERTY_MAXIMUM_PRICE: {
        return AddSmartSearchDialogStep.NEW_PROPERTY_MAXIMUM_PRICE_SPECIFY_SEARCH;
      }
      case AddSmartSearchDialogStep.PROPERTY_SELECTION: {
        if (formValue.typeSelection.type === SmartSearchType.MORTGAGE_BORROW_MORE) {
          return AddSmartSearchDialogStep.REMORTGAGE_BORROW_MORE;
        }
        if (formValue.typeSelection.type === SmartSearchType.MORTGAGE_PAY_FASTER) {
          return AddSmartSearchDialogStep.REMORTGAGE_PAY_FASTER;
        }
        if (formValue.typeSelection.type === SmartSearchType.SPEAK_TO_AN_EXPERT) {
          return AddSmartSearchDialogStep.SPEAK_TO_AN_EXPERT;
        }
        return currentStep;
      }
      case AddSmartSearchDialogStep.REMORTGAGE_PAY_FASTER: {
        return AddSmartSearchDialogStep.REMORTGAGE_PAY_FASTER_SPECIFY_SEARCH;
      }
      case AddSmartSearchDialogStep.REMORTGAGE_BORROW_MORE: {
        return AddSmartSearchDialogStep.REMORTGAGE_BORROW_MORE_SPECIFY_SEARCH;
      }
      case AddSmartSearchDialogStep.NEW_PROPERTY_MAXIMUM_PRICE_SPECIFY_SEARCH:
      case AddSmartSearchDialogStep.REMORTGAGE_BORROW_MORE_SPECIFY_SEARCH:
      case AddSmartSearchDialogStep.REMORTGAGE_PAY_FASTER_SPECIFY_SEARCH:
        this.createSmartSearch();
        return currentStep;
      case AddSmartSearchDialogStep.SPEAK_TO_AN_EXPERT: {
        this.createHelpCase();
        return currentStep;
      }
      default: {
        return currentStep;
      }
    }
  };

  private getPreviousStep = (
    currentStep: AddSmartSearchDialogStep,
    formValue: AddSmartSearchDialogFormRawValues,
  ): AddSmartSearchDialogStep => {
    switch (currentStep) {
      case AddSmartSearchDialogStep.INITIAL: {
        return AddSmartSearchDialogStep.INITIAL;
      }
      case AddSmartSearchDialogStep.NEW_PROPERTY_INVESTMENT_TYPE_SELECTION: {
        return AddSmartSearchDialogStep.INITIAL;
      }
      case AddSmartSearchDialogStep.NEW_PROPERTY_MORTGAGE_PURPOSE_SELECTION: {
        return AddSmartSearchDialogStep.NEW_PROPERTY_INVESTMENT_TYPE_SELECTION;
      }
      case AddSmartSearchDialogStep.NEW_PROPERTY_MAXIMUM_PRICE: {
        if (formValue.newPropertyInvestmentTypeSelection.type === ENUM_PropertyInvestmentType.PROPERTY_INVESTMENT_TYPE_RESIDENTIAL) {
          return AddSmartSearchDialogStep.NEW_PROPERTY_MORTGAGE_PURPOSE_SELECTION;
        }
        return AddSmartSearchDialogStep.NEW_PROPERTY_INVESTMENT_TYPE_SELECTION;
      }
      case AddSmartSearchDialogStep.NEW_PROPERTY_MAXIMUM_PRICE_SPECIFY_SEARCH: {
        return AddSmartSearchDialogStep.NEW_PROPERTY_MAXIMUM_PRICE;
      }
      case AddSmartSearchDialogStep.PROPERTY_SELECTION: {
        return AddSmartSearchDialogStep.INITIAL;
      }
      case AddSmartSearchDialogStep.REMORTGAGE_BORROW_MORE: {
        return AddSmartSearchDialogStep.PROPERTY_SELECTION;
      }
      case AddSmartSearchDialogStep.REMORTGAGE_PAY_FASTER: {
        return AddSmartSearchDialogStep.PROPERTY_SELECTION;
      }
      case AddSmartSearchDialogStep.REMORTGAGE_PAY_FASTER_SPECIFY_SEARCH: {
        return AddSmartSearchDialogStep.REMORTGAGE_PAY_FASTER;
      }
      case AddSmartSearchDialogStep.SPEAK_TO_AN_EXPERT: {
        return AddSmartSearchDialogStep.PROPERTY_SELECTION;
      }
      case AddSmartSearchDialogStep.REMORTGAGE_BORROW_MORE_SPECIFY_SEARCH: {
        return AddSmartSearchDialogStep.REMORTGAGE_BORROW_MORE;
      }
      default: {
        return currentStep;
      }
    }
  };
}
